import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

type RoutePath =
  | "/wissenschaft"
  | "/studien"
  | "/vergleich"
  | "/zulassung"
  | "/nebenwirkungen"
  | "/sicherheit"
  | "/rechner"
  | "/faq"
  | "/thailand";

interface Item {
  to: RoutePath;
  title: { de: string; en: string };
  body: { de: string; en: string };
}

const ALL: Record<RoutePath, Item> = {
  "/wissenschaft": {
    to: "/wissenschaft",
    title: { de: "Wirkmechanismus", en: "Mechanism of action" },
    body: {
      de: "Wie der Triple-Agonismus an GLP-1, GIP und Glucagon zusammenwirkt.",
      en: "How the triple-agonism at GLP-1, GIP and glucagon receptors works together.",
    },
  },
  "/studien": {
    to: "/studien",
    title: { de: "Studienlage & Daten", en: "Trials & data" },
    body: {
      de: "Phase-II- und Phase-III-Daten im Vergleich der drei Wirkstoff-Generationen.",
      en: "Phase II and Phase III data across the three compound generations.",
    },
  },
  "/vergleich": {
    to: "/vergleich",
    title: { de: "Vergleich der Wirkstoffe", en: "Head-to-head comparison" },
    body: {
      de: "Retatrutid vs Semaglutid vs Tirzepatid – Wirkung, Mechanismus, Zulassung.",
      en: "Retatrutide vs Semaglutide vs Tirzepatide — efficacy, mechanism, approval.",
    },
  },
  "/zulassung": {
    to: "/zulassung",
    title: { de: "Zulassungs-Status 2026", en: "Approval status 2026" },
    body: {
      de: "Zeitplan zu FDA, EMA und BfArM sowie Details zur TRIUMPH-1-Studie.",
      en: "Timeline for FDA, EMA and BfArM plus details on the TRIUMPH-1 trial.",
    },
  },
  "/nebenwirkungen": {
    to: "/nebenwirkungen",
    title: { de: "Nebenwirkungen im Detail", en: "Side effects in detail" },
    body: {
      de: "GI-Effekte, Herzfrequenz, seltene Risiken und Kontraindikationen.",
      en: "GI effects, heart rate, rare risks and contraindications.",
    },
  },
  "/sicherheit": {
    to: "/sicherheit",
    title: { de: "Sicherheit & Zugang", en: "Safety & access" },
    body: {
      de: "Sicherheit, ärztliche Begleitung und Zugang über genehmigte klinische Studienprogramme.",
      en: "Safety, medical supervision and access through approved clinical study programs.",
    },
  },
  "/rechner": {
    to: "/rechner",
    title: { de: "Gewichtsverlust-Rechner", en: "Weight-loss calculator" },
    body: {
      de: "Modellhafte Schätzung der Effekte basierend auf Studien-Mittelwerten.",
      en: "Model-based estimate of effects based on trial averages.",
    },
  },
  "/faq": {
    to: "/faq",
    title: { de: "Häufige Fragen (FAQ)", en: "Frequently asked (FAQ)" },
    body: {
      de: "Kompakte Antworten auf die häufigsten Fragen zu Retatrutid.",
      en: "Concise answers to the most common questions about Retatrutide.",
    },
  },
  "/thailand": {
    to: "/thailand",
    title: { de: "Guide für Expats in Thailand", en: "Guide for expats in Thailand" },
    body: {
      de: "Verfügbarkeit, Preise in Baht und Bangkoker Krankenhäuser für GLP-1-Therapien.",
      en: "Availability, prices in Baht and Bangkok hospitals for GLP-1 therapy.",
    },
  },
};

interface Props {
  only: RoutePath[];
}

export function RelatedLinks({ only }: Props) {
  const { lang } = useI18n();
  const de = lang === "de";
  const items = only.map((p) => ALL[p]);

  return (
    <section aria-labelledby="related-links-heading" className="mt-16">
      <h2
        id="related-links-heading"
        className="text-xs font-semibold uppercase tracking-widest text-indigo"
      >
        {de ? "Weiterlesen" : "Read next"}
      </h2>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="card-elevated card-hover group flex h-full flex-col justify-between p-5"
          >
            <div>
              <h3 className="text-base font-semibold text-foreground">
                {de ? it.title.de : it.title.en}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {de ? it.body.de : it.body.en}
              </p>
            </div>
            <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-indigo">
              {de ? "Ansehen" : "Open"}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
